import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ScrollEventData, TableRowRenderer } from 'react-virtualized'
import { defaultRowRenderer } from 'react-virtualized/dist/es/Table'
import { VirtualTableListingStateLess } from '../../../../baseComponents/TwinTable/VirtualTableListing'
import { OnRowClick } from '../../../../baseComponents/TwinTable/VirtualTableListing/types'
import { getNextOrder, saveTableData } from '../../../../baseComponents/TwinTable/functions'
import { DefaultOrder, ParseDataFunc, TableConfiguration } from '../../../../baseComponents/TwinTable/types'
import { memoChecker } from '../../../../utils/globals/components'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { CreateLinkFunctionType, CreateLinkProps, createLink } from '../../../../utils/globals/link'
import { listenSocket, unlistenSocket } from '../../../../utils/globals/socket'
import { addBottomNavAppElement, changeEmployeeParams } from '../../../../utils/reducers/reduxDispatch'
import { buildOrderTaskTable, getTaskCustomFieldsIds, getTaskWhereFromFilters, parseTaskTableHeaderWhereParams } from '../functions'
import { TaskScreenTableBaseTy } from '../types'
import { SelectedTaskColsFiltersHeader } from '../../../../utils/hooks/tasks/useTaskConfigColumns'
import LoaderDecider from '../../../../baseComponents/LoaderDecider'
import { parseTaskColumnsForGetAll } from './functions'
import { getSingleEmployeeParam } from '../../../../utils/reducers/getters'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { flushSync } from 'react-dom'

const DEFAULT_ROWHEIGHT_PX = 48
const DEFAULT_LIMIT_ROWS = 40

interface TaskTableProps extends TaskTableLogicProps {
    name: string
    createRowDataLink: CreateLinkProps
    setSelectedColsFiltersHeader: React.Dispatch<React.SetStateAction<SelectedTaskColsFiltersHeader>>
    memorizedColumns: any
    selectedColsFiltersHeader: SelectedTaskColsFiltersHeader
    floatingTasks: boolean
    simpleDivContainer?: boolean
}

const TaskTable: React.FC<TaskTableProps> = memo(({ name, columns, getDataFrom, defaultOrder, extraWhereParams, floatingTasks, listenSocketStr, rowRenderer, setTableInstance, onRowClick, parseData, maxOffset, createRowDataLink, generalSearchbar, selectedColsFiltersHeader, setSelectedColsFiltersHeader, memorizedColumns, defaultHeaderParams, simpleDivContainer = false }) => {
    const createRowDataLinkStr = JSON.stringify(createRowDataLink)
    const createLinkCallback: CreateLinkFunctionType = useCallback((allRowData) => {
        return createLink(JSON.parse(createRowDataLinkStr), allRowData)
    }, [createRowDataLinkStr])
    const { tableData, tableConfiguration, headerChanges, changeOffsetVirtualTable, openModalTask, tableRef } = useTaskTableLogic({ columns, getDataFrom, defaultOrder, extraWhereParams, setTableInstance, parseData, maxOffset, selectedColsFiltersHeader, generalSearchbar, listenSocketStr, name, setSelectedColsFiltersHeader, defaultHeaderParams })

    const memorizedRowRenderer: TableRowRenderer = useCallback((props) => {
        props.key = props.rowData?.id
        if (Object.keys(props.rowData).length) {
            if (simpleDivContainer) {
                return <div className='cursor-pointer' key={props.key}>{defaultRowRenderer(props)}</div>
            }else if (floatingTasks) {
                return <div className='cursor-pointer' key={props.key} onClick={() => openModalTask(props.rowData?.id)}>{defaultRowRenderer(props)}</div>
            }
            return <Link to={createLinkCallback(props.rowData)} key={props.key} className='twin_elipsis'>{rowRenderer ? rowRenderer(props) : defaultRowRenderer(props)}</Link>
        }
        return defaultRowRenderer(props)
    }, [createLinkCallback, rowRenderer, floatingTasks, openModalTask, simpleDivContainer])
    return (
        <div className={'flex flex-col flex-auto h-1'} ref={tableRef}>
            <div className='flex-auto'>
                <LoaderDecider loading={tableData === null ? true : false}>
                    <VirtualTableListingStateLess {...{ columns: memorizedColumns, tableConfiguration, headerChanges, rowRenderer: memorizedRowRenderer, onRowClick }} setSelectedColumnsAndFilters={setSelectedColsFiltersHeader as any} tableData={tableData} onScroll={changeOffsetVirtualTable} rowHeight={DEFAULT_ROWHEIGHT_PX} headerHeight={48} name={name} selectedFilters={selectedColsFiltersHeader.filters} selectedOrder={selectedColsFiltersHeader.order} />
                </LoaderDecider>
            </div>
        </div>
    )
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['columns', 'defaultOrder', 'extraWhereParams', 'getDataFrom', 'setTableInstance', 'rowRenderer', 'listenSocketStr', 'floatingTasks', 'selectedColsFiltersHeader', 'memorizedColumns']))
interface TaskTableLogicProps extends TaskScreenTableBaseTy {
    generalSearchbar: string
    defaultOrder?: DefaultOrder
    onRowClick?: OnRowClick
    parseData?: ParseDataFunc
    maxOffset?: number
    name?: string
    selectedColsFiltersHeader?: SelectedTaskColsFiltersHeader
    setSelectedColsFiltersHeader?: React.Dispatch<React.SetStateAction<SelectedTaskColsFiltersHeader>>
    defaultHeaderParams: TwinDictionary
}

const useTaskTableLogic = ({ listenSocketStr, setTableInstance, defaultOrder, getDataFrom, extraWhereParams, parseData, maxOffset, columns, generalSearchbar, selectedColsFiltersHeader, setSelectedColsFiltersHeader, name, defaultHeaderParams }: TaskTableLogicProps) => {
    const tableRef = useRef<any>(null)
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar,
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0,
    })
    const [tableData, setTableData] = useState<{ [fieldName: string]: any }[] | null>(null)
    const saveTaskTableDefaultFilterWhenChanges = useMemo(() => getSingleEmployeeParam('saveTaskTableDefaultFilterWhenChanges') === 'on', [])

    const updateSelectedColumnsAndFilters = useCallback(async (values: TwinDictionary | null) => {
        const myName = 'table_' + name
        let isCustomFilter = values?.id && String(values?.id) !== '0' ? true : false
        if (!isCustomFilter && values && saveTaskTableDefaultFilterWhenChanges) {
            delete values.id
            delete values.name
            const table = getSingleEmployeeParam(myName) || '{}'
            const myValuesStr = JSON.stringify(values)
            if (table !== myValuesStr) {
                await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValuesStr })
                changeEmployeeParams({ [myName]: myValuesStr })
            }
        }
    }, [name, saveTaskTableDefaultFilterWhenChanges])

    const updateSingleFieldEmployeeParams = useCallback(async (fieldName: string, value: string | string[]) => {
        if (setSelectedColsFiltersHeader) {
            flushSync(async () => {
                let result = null
                await setSelectedColsFiltersHeader((old) => {
                    const copyOld: SelectedTaskColsFiltersHeader = JSON.parse(JSON.stringify(old))
                    let filters = copyOld?.filters || {}
                    if (filters) {
                        if (value === '' || (Array.isArray(value) && value[0] === '')) {
                            delete filters[fieldName]
                        } else {
                            filters[fieldName] = { value: value }
                        }
                    }
                    const myResult = { ...copyOld, filters }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
          }
      }, [updateSelectedColumnsAndFilters, setSelectedColsFiltersHeader])

    const updateOrderEmployeeParams = useCallback(async (value: string) => {
        if (setSelectedColsFiltersHeader) {
            flushSync(async () => {
                let result = null
                await setSelectedColsFiltersHeader((old) => {
                    const copyOld: SelectedTaskColsFiltersHeader = JSON.parse(JSON.stringify(old))
                    let order = copyOld?.order || {}
                    if (order) {
                        const newOrder = getNextOrder(value, order, defaultOrder?.order || 'desc')
                        if (newOrder[value] === null) {
                            delete newOrder[value]
                        }
                        order = newOrder
                    }
                    const myResult = { ...copyOld, order }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
                
        }
    }, [defaultOrder?.order, updateSelectedColumnsAndFilters, setSelectedColsFiltersHeader])

    const openModalTask = useCallback(async (id: number) => {
        addBottomNavAppElement({ type: 'taskModal', key: 'taskModal-' + id, extraData: { id, opened: true } })
    }, [])

    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
            }
        })
        updateOrderEmployeeParams(fieldName)
    }, [setTableConfiguration, updateOrderEmployeeParams])

    const changeOffset = useCallback((offset: number) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset,
            }
        })
    }, [setTableConfiguration])

    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            if ((Array.isArray(value) && value[0] === '')) {
                value = ''
            }
            return {
                ...copyOld,
                offset: 0
            }
        })
        updateSingleFieldEmployeeParams(fieldName, value)
    }, [setTableConfiguration, updateSingleFieldEmployeeParams])

    const changeOffsetVirtualTable = useCallback((params: ScrollEventData) => {
        const nitems = params.clientHeight / DEFAULT_ROWHEIGHT_PX
        const sub = DEFAULT_ROWHEIGHT_PX * nitems + params.scrollTop
        const actual = sub / DEFAULT_ROWHEIGHT_PX
        if (tableData) {
            const should_update = tableData.length - 15
            if ((maxOffset === undefined || maxOffset > tableConfiguration.offset) && should_update <= actual && tableData.length === tableConfiguration.offset + DEFAULT_LIMIT_ROWS) {
                changeOffset(tableConfiguration.offset + DEFAULT_LIMIT_ROWS)
            }
        }
    }, [tableConfiguration.offset, tableData, changeOffset, maxOffset])

    const myExtraParamsJustForEffect = JSON.stringify(extraWhereParams)
    const myDefaultHeaderParamsJustForEffect = JSON.stringify(defaultHeaderParams)
    const myTableConfigurationJustForEffect = JSON.stringify(tableConfiguration)
    const myColumnsJustForEffect = JSON.stringify(parseTaskColumnsForGetAll(columns))
    const mySelectedColFiltersHeaderJustForEffect = JSON.stringify(selectedColsFiltersHeader)
    const updateDataTable = useCallback(async (params: any) => {
        const { myTableConfiguration, myExtraWhereParams, selectedColsFiltersHeader, defaultHeaderParams } = params
        const myHeaderTableWhereParams = { ...defaultHeaderParams, ...selectedColsFiltersHeader?.filterHeader }
        let orderBuilded = buildOrderTaskTable({ ...selectedColsFiltersHeader?.order }, params.columns)
        if (!orderBuilded.length) {
            orderBuilded = [[params.defaultOrder?.defaultOrderField || 'id', params.defaultOrder?.order || 'desc']]
        }
        const headerTableParsedWhere = parseTaskTableHeaderWhereParams(myHeaderTableWhereParams)
        const customFieldIds = getTaskCustomFieldsIds(params.selectedColsFiltersHeader?.selectedColumns || {})
        let apiTableData = await twinFetchPostJSON(getDataFrom, {
            limit: params.limit,
            customFieldIds,
            offset: myTableConfiguration.offset,
            whereToParse: {
                ...myExtraWhereParams,
                ...headerTableParsedWhere,
                ...getTaskWhereFromFilters(selectedColsFiltersHeader?.filters, params.columns),
            },
            orderToParse: [
                ...orderBuilded
            ],
        })

        if (params.parseData) {
            apiTableData = params.parseData(apiTableData)
        }
        saveTableData(apiTableData, myTableConfiguration.offset, setTableData)

    }, [getDataFrom])


    const updateDataTableBySocket = useCallback(() => {
        const myExtraWhereParams = JSON.parse(myExtraParamsJustForEffect || '{}')
        const myTableConfiguration = JSON.parse(myTableConfigurationJustForEffect || '{}')
        const myColumns = JSON.parse(myColumnsJustForEffect || '{}')
        const mySelectedColsFiltersHeader = JSON.parse(mySelectedColFiltersHeaderJustForEffect || '{}')
        const myDefaultHeaderParams = JSON.parse(myDefaultHeaderParamsJustForEffect || '{}')

        const limit = myTableConfiguration.offset + DEFAULT_LIMIT_ROWS
        myTableConfiguration.offset = 0
        updateDataTable({ myExtraWhereParams, myTableConfiguration, limit, columns: myColumns, defaultOrder, parseData, selectedColsFiltersHeader: mySelectedColsFiltersHeader, defaultHeaderParams: myDefaultHeaderParams })

    }, [defaultOrder, myExtraParamsJustForEffect, mySelectedColFiltersHeaderJustForEffect, parseData, updateDataTable, myTableConfigurationJustForEffect, myColumnsJustForEffect, myDefaultHeaderParamsJustForEffect])

    const getTableDataFromStart = useCallback(() => {
        setTableConfiguration((oldTableConfiguration) => {
            return JSON.parse(JSON.stringify({
                ...oldTableConfiguration,
                petitionNumber: oldTableConfiguration.petitionNumber + 1,
                offset: 0
            }))
        })
        if (tableRef.current) {
            const tableBody = tableRef.current.querySelector('.ReactVirtualized__Table__Grid');
            if (tableBody) {
                tableBody.scrollTop = 0
            }
        }
    }, [setTableConfiguration, tableRef])

    const getUpdateTableByEffect = useCallback(() => {
        const myExtraWhereParams = JSON.parse(myExtraParamsJustForEffect || '{}')
        const myTableConfiguration = JSON.parse(myTableConfigurationJustForEffect || '{}')
        const myColumns = JSON.parse(myColumnsJustForEffect || '{}')
        const mySelectedColsFiltersHeader = JSON.parse(mySelectedColFiltersHeaderJustForEffect || '{}')
        const myDefaultHeaderParams = JSON.parse(myDefaultHeaderParamsJustForEffect || '{}')
        updateDataTable({ myExtraWhereParams, myTableConfiguration, limit: DEFAULT_LIMIT_ROWS, columns: myColumns, defaultOrder, selectedColsFiltersHeader: mySelectedColsFiltersHeader, parseData, defaultHeaderParams: myDefaultHeaderParams })

    }, [myExtraParamsJustForEffect, myTableConfigurationJustForEffect, parseData, updateDataTable, defaultOrder, mySelectedColFiltersHeaderJustForEffect, myColumnsJustForEffect, myDefaultHeaderParamsJustForEffect])

    useEffect(() => {
        if (tableRef.current) {
            const tableBody = tableRef.current.querySelector('.ReactVirtualized__Table__Grid');
            if (tableBody && tableConfiguration.offset === 0) {
                tableBody.scrollTop = 0
            }
        }
    }, [tableConfiguration.offset, tableRef])

    useEffect(() => {
        getUpdateTableByEffect()
    }, [getUpdateTableByEffect])

    useEffect(() => {
        setTableInstance?.({
            getTableDataFromStart,
            changeOffset
        })
    }, [setTableInstance, getTableDataFromStart, changeOffset])

    useEffect(() => {
        listenSocket(listenSocketStr, updateDataTableBySocket, true)
        return () => {
            unlistenSocket(listenSocketStr)
        }
    }, [updateDataTableBySocket, listenSocketStr])

    return { tableData, tableConfiguration, setTableConfiguration, changeOffsetVirtualTable, headerChanges: { changeOrder, changeSingleField }, openModalTask, tableRef }
}

export default TaskTable